import { Credentials } from '../models/Credentials';
import { LoginType } from '../models/LoginType';
import { Pleb } from '../models/Pleb';
import { FakeData } from './FakeData';
import { Repository } from './Repository';
import {
  getNostrLoginUrl,
  NOSTR_AUTH_KIND,
  NostrAuthError,
  NostrSignedEvent,
} from '../utils/NostrUtils';

export enum OtpReason {
  LOGIN = 'login',
  UPDATE = 'update',
}

export class AuthRepository {
  private readonly submittedNostrEventIds = new Map<string, number>();

  async sendOtp(loginType: LoginType, recipient: string, reason: OtpReason = OtpReason.LOGIN): Promise<string> {
    const path = 'v1/auth/otps';

    try {
      const response = await Repository.client.post(path, {
        recipient,
        reason: reason,
        provider: loginType === LoginType.EMAIL ? 'email' : 'nostr'
      });
      return response.data.data._id;
    } catch (err) {
      throw err;
    }
  }

  async verifyOtp(params: { otpId: string; otp: string }): Promise<{ credentials: Credentials; pleb: Pleb }> {
    const path = 'v1/auth/tokens';

    try {
      const response = await Repository.client.post(path, {
        provider: 'otp',
        reason: 'login',
        otpId: params.otpId,
        otp: params.otp
      });
      return {
        credentials: {
          accessToken: response.data.data.accessToken,
          refreshToken: response.data.data.refreshToken
        },
        pleb: Pleb.currentUserFromJson(response.data.data.user)
      }
    } catch (err) {
      throw err;
    }
  }

  async refreshToken(credentials: Credentials): Promise<{ credentials: Credentials; pleb: Pleb }> {
    const path = 'v1/auth/tokens';

    try {
      if(Repository.offlineMode) {
        return {
          credentials: FakeData.getCredentials(),
          pleb: FakeData.getPleb()
        };
      }
      const response = await Repository.client.post(path, {
        provider: 'refreshToken',
        token: credentials.refreshToken
      });
      return {
        credentials: {
          accessToken: response.data.data.accessToken,
          refreshToken: response.data.data.refreshToken
        },
        pleb: Pleb.currentUserFromJson(response.data.data.user)
      };
    } catch (err) {
      throw err;
    }
  }

  async refreshGetStreamToken(): Promise<string> {
    const path = 'v1/auth/streamTokens';

    try {
      if(Repository.offlineMode) {
        return "fakeString";
      }
      const response = await Repository.client.post(path);
      return response.data.data.getStreamToken;
    } catch (err) {
      throw err;
    }
  }

  async loginWithNip07(
    event: NostrSignedEvent,
    isSignup: boolean,
  ): Promise<{ credentials: Credentials; pleb: Pleb; isSignup: boolean }> {
    const loginUrl = getNostrLoginUrl();
    const now = Date.now();
    const expectedTags = [
      ['u', loginUrl],
      ['method', 'POST'],
      ['action', isSignup ? 'signup' : 'login'],
    ];

    if (
      event.kind !== NOSTR_AUTH_KIND
      || event.content !== ''
      || JSON.stringify(event.tags) !== JSON.stringify(expectedTags)
    ) {
      throw new NostrAuthError('The signed Nostr proof does not match this authentication request.');
    }

    for (const [eventId, expiresAt] of this.submittedNostrEventIds) {
      if (expiresAt <= now) {
        this.submittedNostrEventIds.delete(eventId);
      }
    }

    if (this.submittedNostrEventIds.has(event.id)) {
      throw new Error('This Nostr login proof has already been submitted.');
    }

    // Mark before the request starts. A timeout is ambiguous and must never cause
    // the same one-time proof to be sent again.
    this.submittedNostrEventIds.set(event.id, now + 5 * 60 * 1000);

    try {
      const response = await Repository.client.post(loginUrl, {
        event,
        isSignup,
      }, {
        skipAuthentication: true,
        skipAuthRefresh: true,
      });
      const data = response.data?.data ?? response.data;

      return {
        credentials: {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          streamToken: data.getStreamToken,
        },
        pleb: Pleb.currentUserFromJson(data.user),
        isSignup: Boolean(data.isSignup),
      }
    } catch (err) {
      throw err;
    }
  }
}

// Create a singleton instance
export const authRepository = new AuthRepository();
